// ============================================================================
// ADR-0024 — Sectioned narrative report pane (Board's Report)
// ============================================================================
//
// Adapter over the shared SectionedNarrativeEditor for SECTIONS narrative
// components. Adds what POLICY notes do not have:
//
//   · Applicability variants — each question-driven section carries an
//     "applies" and a "not applicable" text. The variant in force is the
//     auditor's per-section override, else the Corporate Facts answer, else
//     the section's default (reportSections.resolveVariant).
//   · An applicability strip above the editor showing which variant is in
//     force and why, with a per-section override.
//
// Workspace keys (ReportWorkspaceData): section_data, added_sections,
// section_order. The host passes the whole workspace and receives it back
// with only those keys replaced, so nothing else in the blob is touched.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import SectionedNarrativeEditor, {
  type SectionedState,
  type SectionTemplate,
} from '../notes/panes/policy/SectionedNarrativeEditor';
import {
  ReportDataContext,
  type DataInsertOptions,
  type ReportDataContextValue,
} from '../notes/panes/policy/TiptapEditor';
import ReportQuestionsPanel, { type AnswerScope } from './ReportQuestionsPanel';
import {
  answerOf,
  collectInputs,
  collectQuestions,
  computeDrift,
  usedDataKeys,
  inAmountMode,
  inputFieldValues,
  inputValueOf,
  type NoteRounding,
  isOmitted,
  parseSectionDefinitions,
  refreshResolvedData,
  resolveVariant,
  seedResolvedData,
  TABLE_LABELS,
  type ResolvedVariant,
} from './reportSections';
import type {
  ReportAddedSection,
  ReportLiveData,
  ReportSectionOverride,
  ReportWorkspaceData,
} from 'api/types/fsa/reportData.types';
import type { CorporateFactsData } from 'api/types/fsa/corporateFacts.types';
import { buildChecklist } from './reportChecklist';
import { isDirect } from './directMode';
import DocumentSidePanel, { readPanelPrefs, writePanelPrefs, type SidePanelTab } from './DocumentSidePanel';
import type { PriorSections } from './priorSections';

interface Props {
  sectionsJson: string | null | undefined;
  workspace: ReportWorkspaceData;
  setWorkspace: (next: ReportWorkspaceData) => void;
  /** Parsed Corporate Facts — applicability answers + the lists derive_from reads. */
  facts: CorporateFactsData | undefined;
  /** ADR-0024 §5 — current values; the editor shows the workspace SNAPSHOT and flags drift against these. */
  liveData?: ReportLiveData | null;
  contentVersion: number;
  readOnly?: boolean;
  /**
   * ADR-0024 A-1 — records an answer to an audit-scope question (the report's
   * question panel). Saved straight to the engagement's facts — not part of
   * the report's Save — so the Auditor's Report and CARO share one answer.
   */
  onSetAuditAnswer?: (code: string, value: boolean | string | number | null, scope: AnswerScope) => Promise<void>;
  /** Which side signs the report — drives the "no signatories" / UDIN checks. */
  signingSide?: 'board' | 'auditor' | 'none';
  /** ADR-0024 N-1 — rail heading ("Note sections" when the pane edits a note). */
  railTitle?: string;
  /** ADR-0024 N-1 — question panel heading ("Note questions" in a note). */
  questionsTitle?: string;
  /** Actual / Rounded — the amounts shown (a note follows the note editor's toggle). */
  amountMode?: 'actual' | 'rounded';
  /** The engagement's rounding factor (for the note's inputs, which are always live). */
  rounding?: NoteRounding | null;
  /** What the document is called in messages ("report" / "note"). */
  documentNoun?: 'report' | 'note';
  /** Controls shown at the start of the document toolbar (mode switch, related editors). */
  toolbarStart?: React.ReactNode;
  /** The Review notes tab's content (reviewer notes — never printed). */
  reviewNotes?: React.ReactNode;
  /** Loads the previous year's version of this document for the Previous year tab. */
  loadPreviousYear?: () => Promise<PriorSections | null>;
}

/** ADR-0024 N-1 — text of an old POLICY-note section the sectioned version has no section for. */
interface LegacySection {
  code: string;
  title: string;
  content?: unknown;
}

/** Plain text of a TipTap doc — paragraphs / list items on their own lines (read-only display). */
function docText(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as { type?: string; text?: string; content?: unknown[]; attrs?: { label?: string } };
  if (n.type === 'text') return n.text ?? '';
  if (n.type === 'fieldChip') return `[${n.attrs?.label ?? 'field'}]`;
  const inner = (n.content ?? []).map(docText);
  const block = n.type === 'paragraph' || n.type === 'listItem' || n.type === 'heading';
  return block ? inner.join('') + '\n' : inner.join('');
}

const SectionedReportPane: React.FC<Props> = ({
  sectionsJson,
  workspace,
  setWorkspace,
  facts,
  liveData,
  contentVersion,
  readOnly = false,
  onSetAuditAnswer,
  signingSide = 'board',
  railTitle = 'Report sections',
  questionsTitle,
  amountMode = 'actual',
  rounding,
  documentNoun = 'report',
  toolbarStart,
  reviewNotes,
  loadPreviousYear,
}) => {
  // ---- data snapshot (saved-as-primary) ---------------------------------
  // ADR-0024 N-2 — section inputs (inputs.*) are always current: read from
  // Corporate Facts, never from the snapshot.
  // Rounded amount mode shows each value's rounded form (path@rounded, roundedRows).
  const dataContext = useMemo<ReportDataContextValue>(() => inAmountMode(
    { ...(workspace.resolved_fields ?? {}), ...inputFieldValues(facts, rounding) },
    workspace.resolved_tables ?? {},
    amountMode,
  ), [workspace.resolved_fields, workspace.resolved_tables, facts, rounding, amountMode]);

  const insertOptions = useMemo<DataInsertOptions | undefined>(() => liveData ? {
    fields: liveData.catalog ?? [],
    tables: Object.keys(liveData.tables ?? {}).map(source => ({ source, label: TABLE_LABELS[source] ?? source })),
  } : undefined, [liveData]);

  // Only values this document's text uses count (see usedDataKeys) — the
  // snapshot also holds tables it never prints.
  const drift = useMemo(
    () => computeDrift(workspace, liveData, usedDataKeys(parseSectionDefinitions(sectionsJson), workspace, facts)),
    [workspace, liveData, sectionsJson, facts],
  );
  const handleRefresh = useCallback(() => {
    if (!liveData) return;
    setWorkspace(refreshResolvedData(workspace, liveData));
  }, [liveData, workspace, setWorkspace]);

  const defs = useMemo(() => parseSectionDefinitions(sectionsJson), [sectionsJson]);
  const sectionData = useMemo(() => workspace.section_data ?? {}, [workspace.section_data]);
  // Direct — the note is edited as plain text: no questions, no answer strips.
  const direct = isDirect(workspace);

  const resolved = useMemo(() => {
    const map = new Map<string, ResolvedVariant>();
    for (const d of defs) map.set(d.code, resolveVariant(d, sectionData[d.code], facts));
    return map;
  }, [defs, sectionData, facts]);

  const templateSections = useMemo<SectionTemplate[]>(() => defs.map(d => {
    const r = resolved.get(d.code)!;
    // The answer in force may carry its own title (e.g. "Basis for Qualified Opinion").
    return { code: d.code, title: (direct ? null : r.title) ?? d.title, defaultContent: r.content, variantKey: r.variant };
  }), [defs, resolved, direct]);

  const state = useMemo<SectionedState>(() => ({
    sectionData: sectionData as SectionedState['sectionData'],
    addedSections: (workspace.added_sections ?? []) as SectionedState['addedSections'],
    order: workspace.section_order,
  }), [sectionData, workspace.added_sections, workspace.section_order]);

  // ---- ADR-0024 R-4b — wording carried from the previous year ------------
  const carried = useMemo(() => new Set(workspace.carried_from_py ?? []), [workspace.carried_from_py]);
  const withoutCarried = (ws: ReportWorkspaceData, codes: string[]): ReportWorkspaceData => {
    if (codes.length === 0 || !ws.carried_from_py?.length) return ws;
    const left = ws.carried_from_py.filter(c => !codes.includes(c));
    const next = { ...ws };
    if (left.length > 0) next.carried_from_py = left;
    else delete next.carried_from_py;
    return next;
  };
  const markReviewed = useCallback((code: string) => {
    setWorkspace(withoutCarried(workspace, [code]));
  }, [workspace, setWorkspace]);

  const handleChange = useCallback((next: SectionedState) => {
    // Editing a carried section's text (or restoring its default) is the
    // review — it stops being "last year's wording".
    const nextData = next.sectionData as Record<string, ReportSectionOverride>;
    const nextAdded = next.addedSections as ReportAddedSection[];
    const edited = [...carried].filter(code => {
      const before = sectionData[code]?.content ?? workspace.added_sections?.find(a => a.local_id === code)?.content;
      const after = nextData[code]?.content ?? nextAdded.find(a => a.local_id === code)?.content;
      return before !== after;
    });
    // seedResolvedData only ADDS missing snapshot keys, so a field the
    // auditor just inserted shows its value at once, while every value
    // already snapshotted stays as saved.
    setWorkspace(seedResolvedData(withoutCarried({
      ...workspace,
      section_data: nextData,
      added_sections: nextAdded,
      section_order: next.order,
    }, edited), liveData));
  }, [workspace, setWorkspace, liveData, carried, sectionData]);

  const setVariantOverride = useCallback((code: string, value: string) => {
    const entry: ReportSectionOverride = { ...(sectionData[code] ?? {}) };
    if (value === 'auto') delete entry.variant_override;
    else entry.variant_override = value;
    const next = { ...sectionData };
    if (Object.values(entry).every(v => v == null || v === '')) delete next[code];
    else next[code] = entry;
    setWorkspace({ ...workspace, section_data: next });
  }, [sectionData, workspace, setWorkspace]);

  const carriedStrip = useCallback((code: string) => carried.has(code) ? (
    <div style={carriedStripStyle}>
      <span style={carriedPillStyle}>From previous year</span>
      <span style={{ flex: 1 }}>This section still has last year's wording — check dates, names and facts for this year.</span>
      {!readOnly && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => markReviewed(code)}>
          Mark reviewed
        </button>
      )}
    </div>
  ) : null, [carried, readOnly, markReviewed]);

  const renderSectionHeader = useCallback((code: string, isCustom: boolean) => {
    const strip = carriedStrip(code);
    const inner = renderVariantStrip(code, isCustom);
    if (!strip) return inner;
    return <>{strip}{inner}</>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriedStrip, defs, resolved, sectionData, readOnly, setVariantOverride, liveData, direct]);

  function renderVariantStrip(code: string, isCustom: boolean) {
    if (isCustom) return null;
    const def = defs.find(d => d.code === code);
    if (!def) return null;
    const r = resolved.get(code)!;
    const refs = def.statutory_refs?.length ? def.statutory_refs.join(' · ') : null;
    const annexureNote = def.part === 'annexure'
      ? <span style={annexurePillStyle} title="Printed after the report's signature block, on its own page, signed again">Annexure</span>
      : null;

    // No question (or Direct): the references sit on the title row instead.
    if (!def.applicability || direct) return null;

    const isAudit = def.applicability.scope === 'audit' || def.applicability.scope === 'note';
    const options = def.applicability.options;
    const edited = sectionData[code]?.content != null;
    const suggestion = liveData?.suggestions?.[def.applicability.question];
    const sourceLabel = isAudit ? AUDIT_SOURCE_LABEL[r.source] : SOURCE_LABEL[r.source];
    return (
      <div style={stripStyle}>
        {annexureNote}
        <span style={r.applies ? appliesPillStyle : naPillStyle}>{r.label}</span>
        <span style={sourceStyle}>{sourceLabel}</span>
        <span style={promptStyle} title={def.applicability.prompt}>{def.applicability.prompt}</span>
        {!readOnly && (
          <select
            className="form-input"
            style={selectStyle}
            value={sectionData[code]?.variant_override ?? 'auto'}
            onChange={e => setVariantOverride(code, e.target.value)}
            title="Override the variant for this section only"
          >
            <option value="auto">{isAudit ? 'Auto (report questions)' : 'Auto (Corporate Facts)'}</option>
            {options
              ? options.map(o => <option key={o.value} value={o.value}>Force: {o.label}</option>)
              : (
                <>
                  <option value="applies">Force: Applies</option>
                  <option value="not_applicable">Force: Not applicable</option>
                </>
              )}
          </select>
        )}
        {edited && (
          <span style={editedHintStyle}>
            Edited text is used whatever the answer — Restore default to switch to the text for another answer.
          </span>
        )}
        {isOmitted(def, r) && (
          <span style={editedHintStyle}>
            Not printed — this section is left out of the report while the answer is "not applicable".
          </span>
        )}
        {suggestion && (
          <span style={suggestionStyle} title={suggestion.note ?? undefined}>
            Suggested from the saved figures: <strong>{suggestion.answer ? 'Applies' : 'Not applicable'}</strong> — {suggestion.reason}
            {suggestion.note ? ' (approximation — hover for details)' : ''}
          </span>
        )}
        {refs && <span style={refsStyle}>{refs}</span>}
      </div>
    );
  }

  const renderRailBadge = useCallback((code: string, isCustom: boolean) => {
    const py = carried.has(code)
      ? <span style={railPyStyle} title="Last year's wording — review">PY</span>
      : null;
    if (isCustom) return py;
    const def = defs.find(d => d.code === code);
    const r = resolved.get(code);
    const annex = def?.part === 'annexure'
      ? <span style={railAnnexStyle} title="Annexure — printed after the signed report">annex</span>
      : null;
    if (!r || r.source === 'always' || direct) return <>{py}{annex}</>;
    return (
      <>
        {py}
        {annex}
        <span
          style={{ ...railDotStyle, background: r.applies ? '#16a34a' : '#94a3b8' }}
          title={def && isOmitted(def, r) ? `${r.label} — not printed` : r.label}
        />
      </>
    );
  }, [defs, resolved, carried, direct]);

  // ---- ADR-0024 A-3 — text typed into a v1 (HTML) report in free-text mode.
  // The saved blob keeps v1's keys when an engagement moves to the sectioned
  // version; the text is shown read-only so nothing typed is lost.
  // ADR-0024 N-1 — the same for a note: text typed into an old POLICY-note
  // section the sectioned version has no section for (legacy_sections).
  // ADR-0024 N-4 — also text saved under a section code the pinned version no
  // longer has (an engagement moved to a newer version, e.g. v1's typed tables
  // replaced by generated grids): kept, shown here, not printed.
  const legacySections = useMemo<LegacySection[]>(() => {
    const kept = (workspace as { legacy_sections?: LegacySection[] }).legacy_sections;
    const list = Array.isArray(kept) ? [...kept] : [];
    const known = new Set([...defs.map(d => d.code), ...(workspace.added_sections ?? []).map(a => a.local_id)]);
    for (const [code, ov] of Object.entries(workspace.section_data ?? {})) {
      if (!known.has(code) && ov?.content && !list.some(l => l.code === code)) {
        list.push({ code, title: ov.custom_title ?? code, content: ov.content });
      }
    }
    return list;
  }, [workspace, defs]);
  const legacyNoteText = legacySections.length > 0
    ? legacySections.map(s => `${s.title}\n${docText(s.content).trim()}`).join('\n\n')
    : null;
  const legacyText = workspace.content_mode === 'FREE_TEXT' && workspace.free_text?.trim()
    ? workspace.free_text
    : legacyNoteText;

  // ---- ADR-0024 A-1 — audit-scope questions, answered in this editor ------
  const auditQuestions = useMemo(() => collectQuestions(defs).filter(q => q.scope === 'audit' || q.scope === 'note'), [defs]);
  const sectionInputs = useMemo(
    () => collectInputs(defs.filter(d => !(sectionData[d.code]?.is_hidden)), code => resolved.get(code)?.variant),
    [defs, resolved, sectionData],
  );

  // ---- checklist (alerts only — never blocks save or print) --------------
  const checklist = useMemo(
    () => buildChecklist(defs, workspace, facts, liveData, signingSide),
    [defs, workspace, facts, liveData, signingSide],
  );
  const warningCount = checklist.filter(i => i.severity === 'warning').length;
  const [focusRequest, setFocusRequest] = useState<{ code: string; nonce: number } | null>(null);

  // ---- document side panel (Questions · Checklist · Changes · Previous year · Review notes)
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const prefs = useMemo(() => readPanelPrefs(), []);
  const [panelOpen, setPanelOpen] = useState(prefs.open);
  const [panelTab, setPanelTab] = useState<string>(prefs.tab ?? 'questions');
  const setOpen = useCallback((open: boolean) => { setPanelOpen(open); writePanelPrefs(open, panelTab); }, [panelTab]);
  const setTab = useCallback((tab: string) => { setPanelTab(tab); writePanelPrefs(panelOpen, tab); }, [panelOpen]);
  const openTab = useCallback((tab: string) => { setPanelTab(tab); setPanelOpen(true); writePanelPrefs(true, tab); }, []);
  const [allQuestions, setAllQuestions] = useState(false);
  // Re-creates the editor after text is put into a section from outside it.
  const [editorBump, setEditorBump] = useState(0);

  const activeDef = defs.find(d => d.code === activeCode) ?? null;
  const activeAdded = (workspace.added_sections ?? []).find(a => a.local_id === activeCode) ?? null;
  const activeTitle = activeDef
    ? (sectionData[activeDef.code]?.custom_title ?? (direct ? null : resolved.get(activeDef.code)?.title) ?? activeDef.title)
    : (sectionData[activeCode ?? '']?.custom_title ?? activeAdded?.title ?? '');

  const showQuestions = !direct && (auditQuestions.length > 0 || sectionInputs.length > 0);
  const sectionQuestions = auditQuestions.filter(q => activeDef?.applicability?.question === q.code);
  const sectionOwnInputs = sectionInputs.filter(i => (activeDef?.inputs ?? []).some(x => x.code === i.code));
  const shownQuestions = allQuestions ? auditQuestions : sectionQuestions;
  const shownInputs = allQuestions ? sectionInputs : sectionOwnInputs;
  const carriedAnswers = new Set(facts?.note_answers_carried ?? []);
  const toAnswer = auditQuestions.filter(q => answerOf(q, facts) === undefined || (q.scope === 'note' && carriedAnswers.has(q.code))).length
    + sectionInputs.filter(i => {
      const v = inputValueOf(i, facts);
      return v === undefined || v === null || (typeof v === 'string' && !v.trim());
    }).length;

  // Previous year — loaded the first time the tab is opened.
  const [prior, setPrior] = useState<PriorSections | null | undefined>(undefined);
  const [priorLoading, setPriorLoading] = useState(false);
  useEffect(() => {
    if (!panelOpen || panelTab !== 'previous' || prior !== undefined || !loadPreviousYear || priorLoading) return;
    setPriorLoading(true);
    loadPreviousYear()
      .then(p => setPrior(p))
      .catch(() => setPrior(null))
      .finally(() => setPriorLoading(false));
  }, [panelOpen, panelTab, prior, loadPreviousYear, priorLoading]);
  const priorSection = useMemo(() => {
    if (!prior || !activeCode) return null;
    const byCode = prior.portable.find(p => p.code != null && p.code === activeCode);
    if (byCode) return byCode;
    const title = (activeTitle ?? '').trim().toLowerCase();
    return title ? prior.portable.find(p => p.title.trim().toLowerCase() === title) ?? null : null;
  }, [prior, activeCode, activeTitle]);
  const copyPrevious = useCallback(() => {
    if (!priorSection?.content || !activeCode) return;
    const next = { ...sectionData, [activeCode]: { ...(sectionData[activeCode] ?? {}), content: priorSection.content } };
    setWorkspace(seedResolvedData({ ...workspace, section_data: next }, liveData));
    setEditorBump(b => b + 1);
  }, [priorSection, activeCode, sectionData, workspace, setWorkspace, liveData]);

  const tabs: SidePanelTab[] = [];
  if (showQuestions) {
    tabs.push({
      id: 'questions', label: 'Questions', short: 'Q', count: toAnswer, warn: toAnswer > 0,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={panelHintStyle}>
              {allQuestions ? `All ${questionsTitle?.toLowerCase() ?? 'questions'}` : `For “${activeTitle || 'this section'}”`}
            </span>
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => setAllQuestions(a => !a)}>
              {allQuestions ? 'This section' : 'All questions'}
            </button>
          </div>
          {shownQuestions.length === 0 && shownInputs.length === 0 ? (
            <div style={panelEmptyStyle}>No question drives this section — its wording is fixed. Edit the text directly, or see all questions.</div>
          ) : (
            <ReportQuestionsPanel
              questions={shownQuestions}
              facts={facts}
              suggestions={liveData?.suggestions}
              readOnly={readOnly || !onSetAuditAnswer}
              onSetAnswer={onSetAuditAnswer}
              title={questionsTitle}
              inputs={shownInputs}
              embedded
            />
          )}
        </div>
      ),
    });
  }
  tabs.push({
    id: 'checklist', label: 'Checklist', short: 'C', count: warningCount, warn: warningCount > 0,
    content: checklist.length === 0 ? (
      <div style={panelEmptyStyle}>Nothing to review — every section has its values, no guidance text is left, and the {documentNoun} can be signed.</div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {checklist.map((item, i) => (
          <button
            key={i}
            type="button"
            style={{ ...checklistItemStyle, cursor: item.sectionCode ? 'pointer' : 'default' }}
            onClick={() => item.sectionCode && setFocusRequest({ code: item.sectionCode, nonce: Date.now() })}
            title={item.sectionCode ? 'Go to the section' : undefined}
          >
            <span style={{ color: item.severity === 'warning' ? '#b45309' : '#64748b', fontWeight: 700 }}>
              {item.severity === 'warning' ? '!' : 'i'}
            </span>
            <span style={{ flex: 1 }}>{item.message}</span>
            {item.sectionCode && <span style={{ color: 'var(--primary-color, #2563eb)', fontSize: '0.7rem' }}>go →</span>}
          </button>
        ))}
      </div>
    ),
  });
  tabs.push({
    id: 'changes', label: 'Changes', short: 'Δ', count: drift.length, warn: drift.length > 0,
    content: drift.length === 0 ? (
      <div style={panelEmptyStyle}>No value used in this {documentNoun} has changed since it was saved.</div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={panelHintStyle}>
          Values used in this {documentNoun} that changed since it was saved. The {documentNoun} prints the saved values until you refresh.
        </div>
        <table style={driftTableStyle}>
          <tbody>
            {drift.map(d => (
              <tr key={`${d.kind}:${d.key}`}>
                <td style={driftTdStyle}><strong>{d.label}</strong></td>
                <td style={driftTdStyle}>
                  {d.kind === 'table'
                    ? <em>table rows changed</em>
                    : <><s>{d.saved || '—'}</s> → <strong>{d.live || '—'}</strong></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!readOnly && (
          <button type="button" className="btn btn-primary btn-sm" onClick={handleRefresh}>Refresh all</button>
        )}
      </div>
    ),
  });
  if (loadPreviousYear || legacyText) {
    tabs.push({
      id: 'previous', label: 'Previous year', short: 'PY', count: legacyText ? 1 : null,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loadPreviousYear && (
            priorLoading ? <div style={panelHintStyle}>Loading the previous year…</div>
            : prior === null ? <div style={panelEmptyStyle}>No previous-year {documentNoun} is linked to this engagement.</div>
            : prior === undefined ? null
            : !priorSection ? (
              <div style={panelEmptyStyle}>“{activeTitle || 'This section'}” is not in the previous year's {documentNoun} ({prior.engagementCode ?? 'previous engagement'}).</div>
            ) : (
              <>
                <div style={panelHintStyle}>
                  “{priorSection.title}” — as in {prior.engagementCode ?? 'the previous year'}{prior.saved ? '' : ' (not saved there — its default text)'}
                  {priorSection.hidden ? ' · hidden there' : ''}
                </div>
                <div style={priorTextStyle}>{docText(priorSection.content).trim() || '(empty)'}</div>
                {!readOnly && (
                  <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={copyPrevious}
                    title="Replace this section's text with last year's (not saved until you Save)">
                    Copy into this section
                  </button>
                )}
              </>
            )
          )}
          {legacyText && (
            <details style={legacyPanelStyle}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                {legacyNoteText && legacyText === legacyNoteText
                  ? `Earlier text of this ${documentNoun} — read-only, not printed`
                  : 'Free text typed before the sectioned version — read-only, not printed'}
              </summary>
              <pre style={legacyPreStyle}>{legacyText}</pre>
            </details>
          )}
        </div>
      ),
    });
  }
  if (reviewNotes) {
    tabs.push({ id: 'notes', label: 'Review notes', short: 'R', content: reviewNotes });
  }
  const activeTab = tabs.some(t => t.id === panelTab) ? panelTab : tabs[0].id;

  // Statutory references + annexure pill, on the section-title row.
  const renderTitleExtra = (code: string, isCustom: boolean) => {
    if (isCustom) return null;
    const def = defs.find(d => d.code === code);
    if (!def) return null;
    const refs = def.statutory_refs?.length ? def.statutory_refs.join(' · ') : null;
    return (
      <>
        {def.part === 'annexure' && (
          <span style={annexurePillStyle} title="Printed after the report's signature block, on its own page, signed again">Annexure</span>
        )}
        {refs && <span style={titleRefsStyle}>{refs}</span>}
      </>
    );
  };
  // Document controls + the values-changed badge, at the end of the title row
  // (no separate toolbar row taking the text's height).
  const headerRight = (toolbarStart || drift.length > 0) ? (
    <>
      {toolbarStart}
      {drift.length > 0 && (
        <button type="button" style={driftBadgeStyle} onClick={() => openTab('changes')}
          title="Values used in the text changed since it was saved — review and refresh">
          <IconSystem.refresh size={13} /> {drift.length} changed
        </button>
      )}
    </>
  ) : null;

  return (
    <ReportDataContext.Provider value={dataContext}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
        <SectionedNarrativeEditor
          renderTitleExtra={renderTitleExtra}
          headerRight={headerRight}
          templateSections={templateSections}
          state={state}
          onChange={handleChange}
          readOnly={readOnly}
          contentVersion={contentVersion * 1000 + editorBump}
          renderSectionHeader={renderSectionHeader}
          renderRailBadge={renderRailBadge}
          railTitle={railTitle}
          dataInsertOptions={readOnly ? undefined : insertOptions}
          focusRequest={focusRequest}
          onSelectedChange={setActiveCode}
          collapsibleRail
          rightPanel={(
            <DocumentSidePanel
              tabs={tabs}
              active={activeTab}
              onActive={setTab}
              open={panelOpen}
              onOpenChange={setOpen}
            />
          )}
        />
      </div>
    </ReportDataContext.Provider>
  );
};

const driftBadgeStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
  border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
};
const panelHintStyle: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };
const panelEmptyStyle: React.CSSProperties = {
  fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px',
};
const priorTextStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.55, color: 'var(--text-primary)',
  background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 12px', maxHeight: 420, overflowY: 'auto',
};
const checklistItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
  padding: '9px 10px', border: '1px solid var(--border-color)', borderRadius: 8,
  background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'left', lineHeight: 1.4,
};
const annexurePillStyle: React.CSSProperties = {
  padding: '1px 8px', borderRadius: 999, background: '#e0e7ff', color: '#3730a3',
  fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.3,
};
const railAnnexStyle: React.CSSProperties = {
  padding: '0 5px', borderRadius: 999, background: '#e0e7ff', color: '#3730a3',
  fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0,
};
const suggestionStyle: React.CSSProperties = { flexBasis: '100%', color: '#1e40af', fontSize: '0.72rem' };
const carriedStripStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
  background: '#f5f3ff', borderBottom: '1px solid #ddd6fe', color: '#4c1d95', fontSize: '0.75rem',
};
const carriedPillStyle: React.CSSProperties = {
  padding: '1px 8px', borderRadius: 999, background: '#ede9fe', color: '#5b21b6',
  fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
};
const railPyStyle: React.CSSProperties = {
  padding: '0 5px', borderRadius: 999, background: '#ede9fe', color: '#5b21b6',
  fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
};

const driftTableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.8rem', width: '100%' };
const driftTdStyle: React.CSSProperties = { padding: '6px 8px 6px 0', verticalAlign: 'top', borderTop: '1px solid var(--border-color)' };

const SOURCE_LABEL: Record<ResolvedVariant['source'], string> = {
  always: '',
  override: 'forced for this section',
  facts: 'from Corporate Facts',
  derived: 'from the entries recorded in Corporate Facts',
  default: 'default — not answered in Corporate Facts',
};

const legacyPanelStyle: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8, background: '#f8fafc', fontSize: '0.78rem',
};
const legacyPreStyle: React.CSSProperties = {
  maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.76rem',
  margin: 0, padding: 8, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 4,
};

const AUDIT_SOURCE_LABEL: Record<ResolvedVariant['source'], string> = {
  always: '',
  override: 'forced for this section',
  facts: 'from the report questions',
  derived: 'from the entries recorded in Corporate Facts',
  default: 'default — not answered yet',
};

const stripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  padding: '6px 14px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
  fontSize: '0.75rem',
};
const pillBase: React.CSSProperties = {
  padding: '1px 8px',
  borderRadius: 999,
  fontWeight: 700,
  fontSize: '0.68rem',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};
const appliesPillStyle: React.CSSProperties = { ...pillBase, background: '#dcfce7', color: '#166534' };
const naPillStyle: React.CSSProperties = { ...pillBase, background: '#f1f5f9', color: '#475569' };
const sourceStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontStyle: 'italic', whiteSpace: 'nowrap' };
const promptStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 160,
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const selectStyle: React.CSSProperties = { width: 'auto', fontSize: '0.75rem', padding: '2px 6px' };
const editedHintStyle: React.CSSProperties = { flexBasis: '100%', color: '#92400e', fontSize: '0.7rem' };
const titleRefsStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: '0.7rem' };
const refsStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontFamily: 'monospace',
  fontSize: '0.68rem',
  marginLeft: 'auto',
};
const railDotStyle: React.CSSProperties = { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 };

export default SectionedReportPane;
